import React from "react";
import { useTranslation } from "react-i18next";

import { StatisticsContext } from "@pages/Statistics/context";

import { useResource } from "@hooks/useResource";

import {
    getTypeParam,
    getStringsArrayUniqueValues,
    getAdvancedPeriodStringParam,
    getRoundedArrayNumbers,
} from "@helpers/general";

import ErrorPage from "@components/Common/ErrorPage";
import CustomErrorBoundary from "@components/Common/CustomErrorBoundary";
import DonutChart from "@pages/Statistics/CommonCharts/DonutChart";
import LegendPopoverWrapper from "@pages/Statistics/CommonCharts/DonutChart/LegendPopoverWrapper";

export default function PercentageTop10SubFamilies() {
    const { t } = useTranslation();
    const [data, setData] = React.useState<number[]>([]);
    const [labels, setLabels] = React.useState<string[]>([]);
    const {
        period,
        type,
        advancedDate,
        selections,
        setPercentageData,
    } = React.useContext(StatisticsContext);

    const { resourceData: top10SubFamiliesData, error } = useResource(
        `${
            process.env.REACT_APP_API_V1_URL
        }/statistics/sub-families/precentage?${getAdvancedPeriodStringParam(
            period,
            advancedDate
        )}&type=${getTypeParam(type)}${
            selections.designation && selections.designation !== "my_favorites"
                ? `&selected=${selections.designation}`
                : ""
        }`
    );

    React.useEffect(() => {
        let localSeries: number[] = [];
        let localFamilies: string[] = [];

        top10SubFamiliesData.data?.forEach((item: any) => {
            localSeries.push(item.value);
            localFamilies.push(item.designation);
        });

        setData(
            getRoundedArrayNumbers(localSeries.filter((item) => item !== 0))
        );
        setLabels(
            getStringsArrayUniqueValues(
                localFamilies.length > 0
                    ? localFamilies.map((item) => t(item))
                    : [t("No data")]
            )
        );
        setPercentageData!(localSeries);
    }, [top10SubFamiliesData, t, setPercentageData]);

    const donutChartTop10SubFamiliesData = {
        data,
        labels:
            labels.length > 0
                ? getStringsArrayUniqueValues(labels).map((label) => t(label))
                : [t("No data")],
    };

    return (
        <React.Fragment>
            {error ? (
                <ErrorPage />
            ) : (
                <CustomErrorBoundary>
                    <DonutChart
                        title={t("Top 10 sub-families")}
                        data={donutChartTop10SubFamiliesData}
                    >
                        <LegendPopoverWrapper
                            title={t("Top 10 sub-families")}
                            labels={labels || []}
                        />
                    </DonutChart>
                </CustomErrorBoundary>
            )}
        </React.Fragment>
    );
}
